import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Card padding variants
 */
type CardPadding = 'none' | 'sm' | 'md' | 'lg';

/**
 * Props for the Card component
 */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card content */
  children: ReactNode;
  /** Padding size */
  padding?: CardPadding;
  /** Enable hover animation */
  hoverable?: boolean;
  /** Add glow effect on hover */
  glowOnHover?: boolean;
  /** Make card pressable with click animation */
  pressable?: boolean;
  /** Custom glow color (hex) */
  glowColor?: string;
}

/**
 * Props for CardHeader component
 */
export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Header content */
  children?: ReactNode;
  /** Title text (optional, alternative to children) */
  title?: string;
  /** Subtitle text */
  subtitle?: string;
  /** Action element (e.g., button) displayed on the right */
  action?: ReactNode;
}

/**
 * Props for CardBody component
 */
export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  /** Body content */
  children: ReactNode;
}

/**
 * Props for CardFooter component
 */
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  /** Footer content */
  children: ReactNode;
  /** Align footer content */
  align?: 'left' | 'center' | 'right' | 'between';
}

/**
 * Padding styles
 */
const paddingStyles: Record<CardPadding, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

/**
 * Footer alignment styles
 */
const alignStyles: Record<NonNullable<CardFooterProps['align']>, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
  between: 'justify-between',
};

/**
 * CardHeader component for Card header section
 */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ children, title, subtitle, action, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'px-6 py-5',
          'border-b border-white/[0.08]',
          'flex items-center justify-between gap-4',
          className
        )}
        {...props}
      >
        <div className="flex-1 min-w-0">
          {title ? (
            <>
              <h3 className="text-lg font-semibold text-surface-900 truncate">{title}</h3>
              {subtitle && (
                <p className="mt-0.5 text-sm text-surface-500 truncate">{subtitle}</p>
              )}
            </>
          ) : (
            children
          )}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

/**
 * CardBody component for Card main content section
 */
export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('p-6', className)} {...props}>
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';

/**
 * CardFooter component for Card footer section
 */
export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ children, align = 'right', className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'px-6 py-5',
          'border-t border-white/[0.08]',
          'flex items-center gap-3',
          alignStyles[align],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

/**
 * Card component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Card hoverable>
 *   <CardHeader title="Analytics" subtitle="Last 7 days" />
 *   <CardBody>
 *     <p>Card content goes here</p>
 *   </CardBody>
 *   <CardFooter>
 *     <Button variant="ghost">Cancel</Button>
 *     <Button variant="primary">Save</Button>
 *   </CardFooter>
 * </Card>
 * ```
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      padding = 'none',
      hoverable = false,
      glowOnHover = false,
      pressable = false,
      glowColor = 'var(--color-primary)',
      className,
      ...props
    },
    ref
  ) => {
    const motionProps: HTMLMotionProps<'div'> = {
      initial: { opacity: 0, y: 10 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.3 },
      whileHover: hoverable
        ? {
            y: -2,
            transition: { duration: 0.2 },
          }
        : undefined,
      whileTap: pressable ? { scale: 0.98 } : undefined,
    };

    return (
      <motion.div
        ref={ref}
        className={cn(
          // Glass effect
          'backdrop-blur-xl',
          'bg-white/[0.08]',
          'border border-white/[0.12]',
          'rounded-2xl',
          'shadow-xl shadow-black/20',
          // Transitions
          'transition-all duration-300',
          // Hover effects
          hoverable && 'hover:bg-white/[0.12] hover:border-white/[0.18]',
          glowOnHover && 'hover:shadow-2xl',
          // Pressable
          pressable && 'cursor-pointer',
          // Padding
          paddingStyles[padding],
          // Overflow
          'overflow-hidden',
          className
        )}
        style={
          glowOnHover
            ? {
                ['--glow-color' as string]: glowColor,
              }
            : undefined
        }
        {...motionProps}
        {...(props as Omit<HTMLMotionProps<'div'>, 'ref'>)}
      >
        {/* Glow effect overlay */}
        {glowOnHover && (
          <div
            className={cn(
              'absolute inset-0 opacity-0 transition-opacity duration-300',
              'hover:opacity-100 pointer-events-none',
              'bg-gradient-to-br from-transparent via-transparent'
            )}
            style={{
              background: `radial-gradient(circle at 50% 0%, color-mix(in srgb, ${glowColor} 8%, transparent), transparent 50%)`,
            }}
          />
        )}
        {children}
      </motion.div>
    );
  }
);

Card.displayName = 'Card';

export default Card;
